"use client";

import { useEffect, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  arithmeticFromFixed,
  daysInPersianYear,
  fixedFromPersian,
  isPersianLeapYear,
  isValidPersianDate,
  marchEquinox,
  type PersianDate,
  persianFromFixed,
  persianMonthLength,
  persianNewYear,
} from "@/lib/tool-kit/calendars/persian";
import {
  isoFromRd,
  parseGregorianIso,
  rdFromGregorian,
  utcDateFromRd,
  weekdayFromRd,
} from "@/lib/tool-kit/calendars/rd";
import { julianFromDays } from "@/lib/tool-kit/converters/hijri";
import { daysFromCivil } from "@/lib/tool-kit/converters/week-number";

/** Month names in Persian script. Data, not prose. */
const PERSIAN_MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
] as const;

/** Weekday names in Persian script, Sunday first. */
const PERSIAN_WEEKDAYS = [
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنجشنبه",
  "جمعه",
  "شنبه",
] as const;

const EXTENDED_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const DIGIT = /\d/g;

function persianDigits(value: number | string): string {
  return String(value).replace(
    DIGIT,
    (digit) => EXTENDED_DIGITS[Number(digit)]
  );
}

const GREGORIAN_REFORM_RD = rdFromGregorian(1582, 10, 15);
const LAST_RD = rdFromGregorian(2999, 12, 31);
const MAX_PERSIAN_YEAR = 2378;

/** English strings. A translated content file overrides these through `ui`. */
export const PERSIAN_CALENDAR_CONVERTER_STRINGS = {
  direction: "Convert",
  toPersian: "Gregorian → Persian",
  toGregorian: "Persian → Gregorian",
  gregorianDate: "Gregorian date",
  today: "Today",
  persianDay: "Day",
  persianMonth: "Month",
  persianYear: "Year (SH)",
  monthNames: [
    "Farvardin",
    "Ordibehesht",
    "Khordad",
    "Tir",
    "Mordad",
    "Shahrivar",
    "Mehr",
    "Aban",
    "Azar",
    "Dey",
    "Bahman",
    "Esfand",
  ],
  persianDate: "{day} {month} {year}",
  persianLabel: "Persian date",
  scriptLabel: "In Persian",
  gregorianLabel: "Gregorian date",
  weekdayLabel: "Day of the week",
  isoLabel: "ISO 8601",
  julianLabel: "Julian calendar",
  monthSpan: "{month} {year} has {length} days: {start} to {end}.",
  yearLeap:
    "{year} is a leap year of {days} days. Esfand has 30 days. The year runs from {start} to {end}.",
  yearCommon:
    "{year} is a common year of {days} days. Esfand has 29 days. The year runs from {start} to {end}.",
  equinoxBefore:
    "The March equinox fell on {date} at {tehran} Tehran time ({utc} UTC), before noon, so Farvardin 1 is that day.",
  equinoxAfter:
    "The March equinox fell on {date} at {tehran} Tehran time ({utc} UTC), after noon, so Farvardin 1 is the next day.",
  arithmeticSame:
    "The 33-year arithmetic rule used by much software gives the same date.",
  arithmeticDiffers:
    "The 33-year arithmetic rule used by much software gives {date} for this day, which differs from the astronomical calendar.",
  julianNote:
    "Dates before 15 October 1582 are entered and shown in the proleptic Gregorian calendar; historians usually quote the Julian date shown here.",
  earlyNote:
    "The solar Hijri calendar was devised in 1079 CE and adopted by law in Iran in 1925. Earlier dates are calculated backwards and were never used at the time.",
  empireNote:
    "From March 1976 to August 1978 Iran numbered years from the reign of Cyrus the Great (the Imperial era), so documents from then show 2535 and 2536 where this calendar has 1355 and 1356.",
  limits:
    "The equinox is computed from published formulas to within a few minutes. When it falls close to noon in Tehran, the Nowruz day depends on the exact instant and, for future years, on the Earth's uncertain rotation — treat distant dates as predictions.",
  pickDate: "Pick a date to convert.",
  invalidGregorian:
    "Choose a real date from 22 March 622 (1 Farvardin SH 1) to 31 December 2999.",
  enterPersian: "Enter a Persian day, month and year.",
  invalidPersianYear: "Enter a Persian year from 1 to 2378.",
  noSuchDay:
    "{month} {year} has only {length} days, so day {day} does not exist.",
  tableHeading: "The months of {year}",
  monthColumn: "Month",
  persianColumn: "Persian",
  startsColumn: "First day",
  daysColumn: "Days",
  copy: "Copy result",
  copied: "Copied!",
  copySummary: "{persian} = {gregorian}",
  numberedMonth: "{number}. {name}",
} as const;

type Strings = typeof PERSIAN_CALENDAR_CONVERTER_STRINGS;
type Direction = "to-persian" | "to-gregorian";
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const LONG_DATE: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
};

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
};

function useFormatters(strings: Strings) {
  const locale = useToolLocale();
  return {
    long: (rd: number) => formatDateTime(locale, utcDateFromRd(rd), LONG_DATE),
    short: (rd: number) =>
      formatDateTime(locale, utcDateFromRd(rd), SHORT_DATE),
    weekday: (rd: number) =>
      formatDateTime(locale, utcDateFromRd(rd), {
        weekday: "long",
        timeZone: "UTC",
      }),
    julian: (rd: number) => {
      const j = julianFromDays(rd - 719_163);
      return formatDateTime(
        locale,
        new Date(daysFromCivil(j.year, j.month, j.day) * 86_400_000),
        { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }
      );
    },
    persian: (date: PersianDate) =>
      t(strings.persianDate, {
        day: String(date.day),
        month: strings.monthNames[date.month - 1] ?? "",
        year: String(date.year),
      }),
  };
}

type Formatters = ReturnType<typeof useFormatters>;

function scriptDate(date: PersianDate, rd: number): string {
  const weekday = PERSIAN_WEEKDAYS[weekdayFromRd(rd)];
  const month = PERSIAN_MONTHS[date.month - 1];
  return `${weekday} ${persianDigits(date.day)} ${month} ${persianDigits(date.year)}`;
}

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function contextLines(
  rd: number,
  date: PersianDate,
  strings: Strings,
  format: Formatters,
  fmt: Fmt
): string[] {
  const start = persianNewYear(date.year);
  const length = daysInPersianYear(date.year);
  const monthStart = fixedFromPersian({ ...date, day: 1 });
  const monthLength = persianMonthLength(date.year, date.month);
  const equinox = marchEquinox(date.year + 621);
  const beforeNoon = equinox.tehranMinutes < 720;
  const arithmetic = arithmeticFromFixed(rd);
  const same =
    arithmetic.year === date.year &&
    arithmetic.month === date.month &&
    arithmetic.day === date.day;
  const lines = [
    t(strings.monthSpan, {
      month: strings.monthNames[date.month - 1] ?? "",
      year: String(date.year),
      length: fmt(monthLength),
      start: format.short(monthStart),
      end: format.short(monthStart + monthLength - 1),
    }),
    t(isPersianLeapYear(date.year) ? strings.yearLeap : strings.yearCommon, {
      year: String(date.year),
      days: fmt(length),
      start: format.short(start),
      end: format.short(start + length - 1),
    }),
    t(beforeNoon ? strings.equinoxBefore : strings.equinoxAfter, {
      date: format.short(equinox.tehranDay),
      tehran: clock(equinox.tehranMinutes),
      utc: clock(equinox.utcMinutes),
    }),
    same
      ? strings.arithmeticSame
      : t(strings.arithmeticDiffers, { date: format.persian(arithmetic) }),
  ];
  if (rd < GREGORIAN_REFORM_RD) {
    lines.push(strings.julianNote);
  }
  if (rd < rdFromGregorian(1925, 1, 1)) {
    lines.push(strings.earlyNote);
  }
  if (date.year >= 1355 && date.year <= 1357) {
    lines.push(strings.empireNote);
  }
  return lines;
}

function MonthTable({
  year,
  month,
  strings,
  format,
  fmt,
}: {
  year: number;
  month: number;
  strings: Strings;
  format: Formatters;
  fmt: Fmt;
}) {
  const months = strings.monthNames.map((name, index) => {
    const number = index + 1;
    return {
      number,
      name,
      start: fixedFromPersian({ year, month: number, day: 1 }),
      length: persianMonthLength(year, number),
    };
  });
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>
        {t(strings.tableHeading, { year: String(year) })}
      </h2>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.monthColumn}
              </th>
              <th className={TH} scope="col">
                {strings.persianColumn}
              </th>
              <th className={TH} scope="col">
                {strings.startsColumn}
              </th>
              <th className={TH} scope="col">
                {strings.daysColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {months.map((entry) => (
              <tr
                className={
                  entry.number === month ? "bg-primary/10 font-medium" : ""
                }
                key={entry.number}
              >
                <td className={TD}>
                  {t(strings.numberedMonth, {
                    number: fmt(entry.number),
                    name: entry.name,
                  })}
                </td>
                <td className={TD} dir="rtl" lang="fa">
                  {PERSIAN_MONTHS[entry.number - 1]}
                </td>
                <td className={TD}>{format.short(entry.start)}</td>
                <td className={TD}>{fmt(entry.length)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ResultPanel({
  rd,
  direction,
  strings,
}: {
  rd: number;
  direction: Direction;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const format = useFormatters(strings);
  const date = persianFromFixed(rd);
  const persianText = format.persian(date);
  const gregorianText = format.long(rd);
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {direction === "to-persian" ? persianText : gregorianText}
        </h2>
        <StatGrid>
          <StatCard
            emphasis={direction === "to-persian"}
            label={strings.persianLabel}
            value={persianText}
          />
          <StatCard
            label={strings.scriptLabel}
            value={
              <span dir="rtl" lang="fa">
                {scriptDate(date, rd)}
              </span>
            }
          />
          <StatCard
            emphasis={direction === "to-gregorian"}
            label={strings.gregorianLabel}
            value={format.short(rd)}
          />
          <StatCard label={strings.weekdayLabel} value={format.weekday(rd)} />
          <StatCard label={strings.isoLabel} value={isoFromRd(rd)} />
          {rd < GREGORIAN_REFORM_RD ? (
            <StatCard label={strings.julianLabel} value={format.julian(rd)} />
          ) : null}
        </StatGrid>
        <ul className="space-y-1 text-foreground text-sm leading-relaxed">
          {contextLines(rd, date, strings, format, fmt).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={t(strings.copySummary, {
            persian: persianText,
            gregorian: gregorianText,
          })}
        />
      </section>
      <Notice>{strings.limits}</Notice>
      <MonthTable
        fmt={fmt}
        format={format}
        month={date.month}
        strings={strings}
        year={date.year}
      />
    </>
  );
}

interface PersianFields {
  day: string;
  month: string;
  year: string;
}

type Reading = { rd: number } | { error: string } | null;

function readGregorian(text: string, strings: Strings): Reading {
  if (text.trim() === "") {
    return null;
  }
  const rd = parseGregorianIso(text);
  if (rd === null || rd < persianNewYear(1) || rd > LAST_RD) {
    return { error: strings.invalidGregorian };
  }
  return { rd };
}

function readPersian(
  fields: PersianFields,
  strings: Strings,
  fmt: Fmt
): Reading {
  if (fields.day.trim() === "" || fields.year.trim() === "") {
    return null;
  }
  const day = parseIntegerInput(fields.day);
  const month = Number(fields.month);
  const year = parseIntegerInput(fields.year);
  if (year === null || year < 1 || year > MAX_PERSIAN_YEAR) {
    return { error: strings.invalidPersianYear };
  }
  if (day === null || day < 1 || day > 31) {
    return { error: strings.enterPersian };
  }
  if (!isValidPersianDate({ year, month, day })) {
    return {
      error: t(strings.noSuchDay, {
        month: strings.monthNames[month - 1] ?? "",
        year: String(year),
        length: fmt(persianMonthLength(year, month)),
        day: fmt(day),
      }),
    };
  }
  const rd = fixedFromPersian({ year, month, day });
  return rd > LAST_RD ? { error: strings.invalidPersianYear } : { rd };
}

/** Today on the reader's own clock — only ever called after hydration. */
function todayRd(): number {
  const now = new Date();
  return rdFromGregorian(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export default function PersianCalendarConverterWidget() {
  const strings = useToolUi(PERSIAN_CALENDAR_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [direction, setDirection] = useState<Direction>("to-persian");
  const [gregorian, setGregorian] = useState("");
  const [fields, setFields] = useState<PersianFields>({
    day: "",
    month: "1",
    year: "",
  });

  useEffect(() => {
    const today = todayRd();
    const date = persianFromFixed(today);
    setGregorian((current) => current || isoFromRd(today));
    setFields((current) =>
      current.year === ""
        ? {
            day: String(date.day),
            month: String(date.month),
            year: String(date.year),
          }
        : current
    );
  }, []);

  const reading =
    direction === "to-persian"
      ? readGregorian(gregorian, strings)
      : readPersian(fields, strings, fmt);
  const setField = (key: keyof PersianFields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="pc-direction"
        onChange={setDirection}
        options={[
          { value: "to-persian", label: strings.toPersian },
          { value: "to-gregorian", label: strings.toGregorian },
        ]}
        value={direction}
      />
      {direction === "to-persian" ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <TextField
              id="pc-gregorian"
              label={strings.gregorianDate}
              onChange={setGregorian}
              type="date"
              value={gregorian}
            />
          </div>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setGregorian(isoFromRd(todayRd()))}
            type="button"
          >
            {strings.today}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[1fr_2fr_1fr]">
          <TextField
            id="pc-day"
            label={strings.persianDay}
            numeric
            onChange={setField("day")}
            value={fields.day}
          />
          <SelectField
            id="pc-month"
            label={strings.persianMonth}
            onChange={setField("month")}
            options={strings.monthNames.map((name, index) => ({
              value: String(index + 1),
              label: t(strings.numberedMonth, { number: fmt(index + 1), name }),
            }))}
            value={fields.month}
          />
          <TextField
            id="pc-year"
            label={strings.persianYear}
            numeric
            onChange={setField("year")}
            value={fields.year}
          />
        </div>
      )}
      {reading === null ? (
        <Notice>
          {direction === "to-persian" ? strings.pickDate : strings.enterPersian}
        </Notice>
      ) : null}
      {reading !== null && "error" in reading ? (
        <ErrorNotice>{reading.error}</ErrorNotice>
      ) : null}
      {reading !== null && "rd" in reading ? (
        <ResultPanel direction={direction} rd={reading.rd} strings={strings} />
      ) : null}
    </div>
  );
}
